<template>
  <div id="atendimentos-pessoa">
    <Menu />
    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Atendimentos pessoa"
            description="Atendimentos por pessoa realizados nos últimos 30 dias"
            url=""
            :enabledModalInformacao="true"
            :enabledIframeModalInformacao="true"
            :urlIframeModalInformacao="urlAjuda"
            pageIdentifier="atendimentos-pessoa"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="filter-select">
            <vue-select
              :options="estabelecimentos"
              label="no_unidade_saude"
              placeholder="Unidade de saúde"
              @input="onChangeEstabelecimento"
              v-model="selectedEstabelecimento"
              :loading="loadingSelectEstabelecimento"
              :disabled="loadingSelectEstabelecimento"
            >
              <template slot="option" slot-scope="option">
                {{ option.no_unidade_saude }}
              </template>
              <div slot="no-options">Não há opções correspondentes</div>
            </vue-select>
            <button
              class="btn btn-filter"
              type="button"
              :disabled="loading"
              @click="buscarDados"
            >
              Buscar
              <b-spinner
                label="Spinning"
                small
                variant="white"
                v-if="loading"
              ></b-spinner>
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="info-table py-4">
            <div class="info-table-item">
              <Subtitle
                @exibirVermelhos="handleExibirVermelhos"
                @exibirVerdes="handleExibirVerdes"
                :setRed="setRed"
              />
            </div>
            <div class="info-table-item">
              <DownloadFiles
                :pdfEnabled="false"
                :callback-pdf="generatePdf"
                :data="displayedCadastros"
                :fields="dataFields"
                name="atendimentos-pessoa"
              />
            </div>
          </div>
          <TabelaCadastros
            ref="table"
            :cadastros="displayedCadastros"
            :selectedEstabelecimento="selectedEstabelecimento"
            :loading="loading"
          />
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle.vue'
import Subtitle from './Subtitle.vue'
import * as Filter from './filter.js'
import * as Functions from './functions.js'
import { buildDataFields } from './utils.js'
import utils from '@/mixins/utils.js'
import DownloadFiles from '@/components/utils/DownloadFiles.vue'
import TabelaCadastros from './TabelaCadastros.vue'

const AJUDAS = {
  upa_atendimentos_pessoa:
    'https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-2711',
  vinculo_acompanhamento_atendimentos_pessoa:
    'https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-2751',
}

export default {
  name: 'AtendimentosPessoa',
  props: {
    ajuda: {
      type: String,
      default: '',
    },
  },
  components: {
    Menu,
    Footer,
    SectionTitle,
    Subtitle,
    DownloadFiles,
    TabelaCadastros,
  },
  mixins: [utils],
  data() {
    return {
      loadingSelectEstabelecimento: false,
      estabelecimentos: [],
      selectedEstabelecimento: null,
      cadastros: [],
      cadastrosOriginais: [],
      loading: false,
      setRed: false,
      exibirCadastrosVermelhos: false,
      exibirCadastrosVerdes: false,
      filterSearchTable: false,
    }
  },
  computed: {
    dataFields() {
      return buildDataFields(this.selectedEstabelecimento)
    },

    displayedCadastros() {
      if (this.filterSearchTable) return this.filterSearchTable

      const resultado = Filter.filtro(
        this.cadastros,
        this.exibirCadastrosVermelhos,
        this.exibirCadastrosVerdes,
        this.selectedEstabelecimento
      )
      return resultado || []
    },

    urlAjuda() {
      const key = this.$route.query.ajudaKey
      return key ? AJUDAS[key] || '' : ''
    },
  },
  methods: {
    /**
     * Habilita os filtros vermelho/verde
     *
     * @param {Boolean} vermelho
     * @param {Boolean} verde
     */
    filtros(vermelho, verde) {
      if (this.$refs.table) this.$refs.table.search = ''
      this.setRed = vermelho
      this.exibirCadastrosVermelhos = vermelho
      this.exibirCadastrosVerdes = verde && !vermelho
    },

    /**
     * Atualiza o estado do filtro de busca da tabela.
     * @param {Object} item Item selecionado
     */
    filterSearch(item) {
      this.filterSearchTable = item.filter || false
    },

    /**
     * Atualiza o filtro para exibir registros classificados como vermelhos.
     * @param {Boolean} valor Indica se o filtro deve ser aplicado
     */
    handleExibirVermelhos(valor) {
      this.filtros(valor, false)
    },

    /**
     * Atualiza o filtro para exibir registros classificados como verdes.
     * @param {Boolean} valor Indica se o filtro deve ser aplicado
     */
    handleExibirVerdes(valor) {
      this.filtros(false, valor)
    },

    /**
     * Buscar dados dos atendimentos
     */
    async buscarDados() {
      try {
        if (this.selectedEstabelecimento == null) return

        this.loading = true

        const hoje = new Date()

        const data = new Date(hoje)
        data.setDate(data.getDate() - 31)

        const co_dim_tempo =
          data.getFullYear().toString() +
          String(data.getMonth() + 1).padStart(2, '0') +
          String(data.getDate()).padStart(2, '0')

        const response = await this.$api.get(
          `/app/atendimentos-pessoa/${this.$store.state.cod_ibge}/${this.selectedEstabelecimento.co_ubs}/${co_dim_tempo}`
        )
        if (response) {
          this.cadastrosOriginais = response.data
          this.cadastros = response.data
          this.filtros(true, false)
        }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    /**
     * Manipula a alteração do estabelecimento selecionado.
     *
     * @param {Object} item estabelecimento selecionado
     */
    async onChangeEstabelecimento(item) {
      this.selectedEquipe = null
      if (item == null) {
        this.cadastros = this.cadastrosOriginais
        return
      }
      this.cadastros = this.cadastrosOriginais.filter((el) => {
        return el.co_ubs != null && el.co_ubs == item.co_ubs
      })
    },

    /**
     * Gera o relatório em PDF.
     */
    generatePdf() {
      Functions.generatePdf(
        this.displayedCadastros,
        this.equipe,
        this.microArea
      )
    },

    /**
     * Carrega os estabelecimentos no select
     */
    async carregarSelectEstabelecimento() {
      try {
        this.loadingSelectEstabelecimento = true
        let response = await this.$api.get(
          `/app/relatorio-atendimentos/${this.$store.state.cod_ibge}/unidades-de-saude`
        )
        if (response) {
          this.estabelecimentos = response.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingSelectEstabelecimento = false
      }
    },
  },
  async created() {
    await this.carregarSelectEstabelecimento()
  },
}
</script>

<style scoped></style>
